import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { Box, Typography, Button, CircularProgress, Skeleton } from "@mui/material";
import { getAuthToken } from "../../../utils/getAuthToken";
import connectionMonitor from "../../../utils/connectionMonitor";
import { useContactSelection } from "../../../hooks/useContactSelection";
import { focusIncomingCall } from "../../../redux/Slice/Calls/callSlice";
import { selectLiveCallsList, selectLiveCallsHydrated, selectAnswerableCallIds } from "../../../redux/selectors";
import { fetchLifecycleStages } from "../../../redux/Slice/Lifecycle/lifecycleStageSlice";
import LiveCallCard from "./LiveCallCard";
import InboxFilterDropdown from "../Shared/InboxFilterDropdown";
import { getFromLocalStorage, setToLocalStorage } from "../../../utils/localStorage";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const LOGS_PAGE_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 350;
// Show/Sort filter state, persisted across refreshes and logins (same
// browser) — same convention as InboxAll.jsx's CHATS_FILTER_STORAGE_KEY.
const CALLS_FILTER_STORAGE_KEY = "inbox_calls_filters";

// Exactly the five states the sidebar needs at a glance — "live" is the
// realtime view (liveCallsSlice); the rest are thin params onto the same
// paginated /api/calling/logs endpoint the full Calls page table already uses.
// "ended" isn't a real CallModel status — callingController.js's getCallLogs
// expands it server-side into "anything not currently ringing/connecting/connected".
const FILTERS = [
  { key: "all", label: "All" },
  { key: "missed", label: "Missed", params: { status: "missed" } },
  { key: "abandoned", label: "No answer", params: { status: "abandoned" } },
  { key: "live", label: "Ongoing" },
  { key: "ended", label: "Ended", params: { status: "ended" } },
];

async function fetchCallLogs(params) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}/api/calling/logs`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
}

// searchValue is owned by InboxAll.jsx's shared header now (one search icon
// for both Chats and Calls instead of Calls having its own separate row) —
// this component just debounces whatever value it's handed.
export default function CallsSectionBody({ searchValue = "" }) {
  const dispatch = useDispatch();
  const { selectContact } = useContactSelection();
  const liveCalls = useSelector(selectLiveCallsList);
  const liveHydrated = useSelector(selectLiveCallsHydrated);
  const answerableCallIds = useSelector(selectAnswerableCallIds);
  const userPermissions = useSelector((state) => state.userDetails.userPermissions);
  // Lifecycle stages — same cached/guarded fetch MessageCard.jsx's row uses,
  // needed so LiveCallCard's LifecycleStageChip has real stage data to
  // resolve a contact's lifecycleStageId against.
  const lifecycleStages = useSelector((s) => s.lifecycleStage.stages);
  useEffect(() => {
    if (!lifecycleStages.length) dispatch(fetchLifecycleStages());
  }, [dispatch, lifecycleStages.length]);
  // Inbound (Calls.Add) is what the backend's roleauth("Calls.Add") on
  // answer/reject/ring-state actually enforces — without it those requests
  // can only ever 403, so don't offer the buttons in the first place.
  const inboundAllowed = userPermissions == null || userPermissions?.permissions?.Calls?.Add === true;

  // Restored once on mount from localStorage — see the persistence effect
  // below. Guards against a stale persisted key no longer matching FILTERS
  // (e.g. after a future release changes the available filters).
  const [filter, setFilter] = useState(() => {
    const saved = getFromLocalStorage(CALLS_FILTER_STORAGE_KEY, {})?.filter;
    return FILTERS.some((f) => f.key === saved) ? saved : "all";
  });
  const [sortOrder, setSortOrder] = useState(
    () => getFromLocalStorage(CALLS_FILTER_STORAGE_KEY, {})?.sortOrder ?? "newest"
  ); // 'newest' | 'oldest'
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const searchDebounceRef = useRef(null);

  const activeFilterDef = FILTERS.find((f) => f.key === filter) || FILTERS[0];

  useEffect(() => {
    setToLocalStorage(CALLS_FILTER_STORAGE_KEY, { filter, sortOrder });
  }, [filter, sortOrder]);

  // Server-side search only for the historical filters — /api/calling/logs
  // already does an indexed, anchored prefix match (see callingController.js),
  // never a full collection scan. The live filter never hits the network for
  // this at all (see below): it's a handful of already-in-Redux rows.
  const loadPage = useCallback(
    async (targetPage, append) => {
      if (activeFilterDef.key === "live") return;
      const thisRequestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const result = await fetchCallLogs({
          page: targetPage,
          limit: LOGS_PAGE_LIMIT,
          ...activeFilterDef.params,
          search: search || undefined,
          sort: sortOrder === "oldest" ? "asc" : "desc",
        });
        if (thisRequestId !== requestIdRef.current) return; // superseded by a newer filter/search change
        setRows((prev) => (append ? [...prev, ...result.data] : result.data));
        setHasMore(result.hasMore);
        setPage(targetPage);
      } catch (err) {
        // best-effort — leave whatever was already on screen
      } finally {
        if (thisRequestId === requestIdRef.current) setLoading(false);
      }
    },
    [activeFilterDef, search, sortOrder]
  );

  // Re-fetch from page 1 whenever the filter, sort, or the (debounced) search term changes.
  useEffect(() => {
    if (filter === "live") return;
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search, sortOrder]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(searchValue.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchValue]);

  // Live filter: filter the already-fetched/socket-maintained liveCalls array
  // in memory — a handful of rows at most (concurrent lines on one workspace),
  // so a client-side substring match is both correct and effectively free,
  // with zero additional network/DB cost per keystroke.
  const filteredLiveCalls = useMemo(() => {
    if (!search) return liveCalls;
    const term = search.toLowerCase();
    return liveCalls.filter(
      (c) => (c.contactName || "").toLowerCase().includes(term) || (c.waNumber || "").toLowerCase().includes(term)
    );
  }, [liveCalls, search]);

  // Clicking any call opens its chat via the same unified selection path
  // every other contact-open flow in the app uses (MessageCard, Add Contact,
  // etc) — dispatches setselectedContactID/fetchMessagesByContact, which is
  // what ChatBody's own effect actually watches. A plain navigate() only
  // changes the URL and renders nothing, since ChatBody doesn't drive off
  // the route directly.
  // For a ringing call this agent's tab actually received (answerableCallIds
  // — excludes ring-group calls this agent wasn't targeted for), this ALSO
  // brings that specific call's Accept/Reject into the corner widget —
  // overriding whichever call auto-won the widget slot, since this is a
  // deliberate pick, not a race. A connected/historical call has nothing to
  // focus, just the chat.
  const handleCardClick = (call) => {
    if (!call?.contactId) return;
    const isRingingAndMine =
      call.status === "ringing" && call.direction === "inbound" && answerableCallIds.includes(call.callId);
    if (isRingingAndMine) {
      dispatch(
        focusIncomingCall({
          callId: call.callId,
          from: call.waNumber,
          contactId: call.contactId,
          contactName: call.contactName,
        })
      );
    }
    selectContact(call.contactId, { navigate: true, clearPrevious: true });
  };

  // Same connectionMonitor pub/sub pattern CallButton.jsx uses for
  // call:requestDial — useCallConnection (mounted once in CallManager) is
  // the only place that actually owns pendingIncomingCallsRef/the
  // RTCPeerConnection, so this card can't answer/reject directly; it just
  // asks that single instance to do it for a specific callId.
  const handleAnswer = (callId) => connectionMonitor.emit("call:requestAnswer", { callId });
  const handleReject = (callId) => connectionMonitor.emit("call:requestReject", { callId });
  // sfu-auto calls only — already connected, just needs an agent's media to join.
  const handleJoin = (call) =>
    connectionMonitor.emit("call:requestJoin", {
      callId: call.callId,
      contactId: call.contactId,
      contactName: call.contactName,
      waNumber: call.waNumber,
      // The call's real connect time (it auto-accepted before any agent
      // joined) — lets the widget's timer show true elapsed duration instead
      // of restarting from zero at the moment this agent clicks Join.
      connectedAt: call.connectedAt,
    });
  // Recovery path for a call that's genuinely still ringing but never
  // reached this tab's widget (missed push, ring-group call, tab opened
  // mid-ring) — re-fetches the ring payload from the backend and opens the
  // real widget exactly as if the live push had just arrived.
  const handleOpenRinging = (callId) => connectionMonitor.emit("call:requestOpenRinging", { callId });

  const isLiveFilter = filter === "live";

  // A ringing/connected call must be actionable (Accept/Reject, Join, the
  // ticking timer) from its card no matter which filter tab happens to be
  // selected — not only while "Ongoing" is active. liveCalls is keyed by
  // callId; any row that also has a live entry uses that fresher data and is
  // treated as live for its own card, independent of the tab-wide isLiveFilter.
  const liveById = useMemo(() => new Map(liveCalls.map((c) => [c.callId, c])), [liveCalls]);

  // "All" is the only historical filter a still-live call's own status could
  // ever match (Missed/No answer/Ended all query a specific terminal status
  // server-side, which a ringing/connecting/connected call never has) — so
  // it's the only one that needs this. Two gaps this closes, both because
  // `rows` only ever comes from a point-in-time REST fetch and nothing
  // re-fetches it just because a socket event arrived:
  //  1) A call that started ringing after the last fetch is otherwise
  //     invisible until something else happens to re-fetch (e.g. switching
  //     away and back) — merge in any liveCalls entry `rows` doesn't know
  //     about yet.
  //  2) A row that WAS live when fetched but has since ended (answered
  //     elsewhere, declined, hung up) would otherwise sit frozen forever
  //     showing its stale ringing/connected chip — drop it once it's no
  //     longer in liveCalls rather than show stale state; the next natural
  //     refetch brings back its accurate, final historical row.
  const LIVE_STATUSES = useMemo(() => new Set(["ringing", "connecting", "connected"]), []);
  const displayRows = useMemo(() => {
    if (isLiveFilter) return filteredLiveCalls;
    if (filter !== "all") return rows;
    const rowIds = new Set(rows.map((r) => r.callId));
    const liveOnly = liveCalls.filter((c) => !rowIds.has(c.callId));
    const stillValid = rows.filter((r) => !LIVE_STATUSES.has(r.status) || liveById.has(r.callId));
    return [...liveOnly, ...stillValid];
  }, [isLiveFilter, filteredLiveCalls, filter, rows, liveCalls, liveById, LIVE_STATUSES]);

  // Instant local feedback after changing a call row's Lifecycle stage —
  // same "patch the already-fetched list" pattern MessageCard's own
  // onLifecycleChanged uses for Chats. Only meaningful for `rows` (the
  // historical REST page); liveCalls/mock rows aren't persisted lists.
  const handleLifecycleChanged = useCallback((contactId, newStageId) => {
    setRows((prev) => prev.map((r) => (r.contactId === contactId ? { ...r, lifecycleStageId: newStageId } : r)));
  }, []);

  const showEmpty = !loading && displayRows.length === 0 && (isLiveFilter ? liveHydrated : true);
  const showSkeleton = isLiveFilter ? !liveHydrated && liveCalls.length === 0 : loading && rows.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          // Left inset matches the Chats section's own "All, Oldest" filter
          // row (InboxAll.jsx: outer section padding + the Tab's own 12px
          // internal padding) — this row lives outside that padded section
          // entirely, so it needs the same total inset spelled out here
          // instead of inheriting it (Rahim, 2026-08-20 — was flush-left,
          // reading as out of formation vs. Chats' "All, Newest" row).
          pl: { xs: "18px", sm: "18px", md: "24px", lg: "26px", xl: "28px" },
          pr: { xs: "6px", sm: "6px", md: "12px", lg: "14px", xl: "16px" },
          pt: 1,
          pb: 1,
        }}
      >
        {/* Sort intentionally only affects the historical filters below —
            "Ongoing" keeps its own priority order (ringing calls surfaced
            above already-connected ones), which a plain newest/oldest
            toggle would otherwise silently break. */}
        <InboxFilterDropdown
          showValue={filter}
          onShowChange={setFilter}
          showOptions={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          sortValue={sortOrder}
          onSortChange={setSortOrder}
          sortOptions={[
            { value: "newest", label: "Newest" },
            { value: "oldest", label: "Oldest" },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {showSkeleton &&
          [...Array(4)].map((_, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.75, px: 1.5, minHeight: { xs: "115px", sm: "88px", md: "115px", lg: "115px", xl: "115px" }, boxSizing: "border-box", borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Skeleton variant="circular" width={50} height={50} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Row 1: name — timestamp, matches the real row's space-between */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Skeleton variant="text" width="50%" height={18} />
                  <Skeleton variant="text" width={40} height={14} />
                </Box>
                {/* Row 2: status icon + colored label */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.25 }}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width="35%" height={14} />
                </Box>
                {/* Row 3: agent avatar + attribution + Lifecycle chip */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
                  <Skeleton variant="circular" width={15} height={15} />
                  <Skeleton variant="text" width="30%" height={12} />
                  <Skeleton variant="rounded" width={48} height={16} sx={{ borderRadius: "6px" }} />
                </Box>
              </Box>
            </Box>
          ))}

        {showEmpty && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              px: 2,
              color: "text.secondary",
            }}
          >
            <Box sx={{ mb: 2, color: (theme) => theme.palette.text.secondary, display: "flex", justifyContent: "center" }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none">
                <g>
                  <path d="M17.45 22.75C16.32 22.75 15.13 22.48 13.9 21.96C12.7 21.45 11.49 20.75 10.31 19.9C9.13 19.04 8 18.07 6.94 17.02C5.88 15.95 4.92 14.82 4.07 13.66C3.21 12.46 2.52 11.27 2.03 10.1C1.51 8.87 1.25 7.67 1.25 6.54C1.25 5.76 1.39 5.02 1.66 4.33C1.95 3.61 2.39 2.96 3 2.39C3.77 1.64 4.65 1.25 5.59 1.25C5.98 1.25 6.38 1.34 6.72 1.5C7.11 1.68 7.44 1.95 7.68 2.31L10 5.58C10.21 5.87 10.37 6.15 10.48 6.43C10.61 6.73 10.68 7.03 10.68 7.32C10.68 7.7 10.57 8.07 10.36 8.42C10.21 8.69 9.98 8.98 9.69 9.27L9.01 9.98C9.02 10.01 9.03 10.03 9.04 10.05C9.16 10.26 9.4 10.62 9.86 11.16C10.34 11.72 10.81 12.22 11.27 12.7C11.87 13.29 12.35 13.74 12.81 14.12C13.38 14.6 13.75 14.84 13.97 14.95L13.95 15L14.68 14.28C14.99 13.97 15.29 13.74 15.58 13.59C16.13 13.25 16.83 13.19 17.53 13.48C17.79 13.59 18.07 13.74 18.37 13.95L21.69 16.31C22.05 16.56 22.33 16.88 22.49 17.26C22.64 17.64 22.71 17.99 22.71 18.34C22.71 18.82 22.6 19.3 22.39 19.75C22.18 20.2 21.92 20.59 21.59 20.95C21.02 21.58 20.4 22.03 19.68 22.32C18.99 22.6 18.24 22.75 17.45 22.75ZM5.59 2.75C5.04 2.75 4.53 2.99 4.04 3.47C3.59 3.9 3.26 4.37 3.06 4.88C2.85 5.4 2.75 5.95 2.75 6.54C2.75 7.47 2.97 8.48 3.41 9.52C3.86 10.58 4.49 11.68 5.29 12.78C6.09 13.88 7 14.95 8 15.96C9 16.95 10.08 17.87 11.19 18.68C12.27 19.47 13.38 20.11 14.48 20.57C16.19 21.3 17.79 21.47 19.11 20.92C19.62 20.71 20.07 20.39 20.48 19.93C20.71 19.68 20.89 19.41 21.04 19.09C21.16 18.84 21.22 18.58 21.22 18.32C21.22 18.16 21.19 18 21.11 17.82C21.09 17.77 21.02 17.65 20.83 17.52L17.51 15.16C17.31 15.02 17.13 14.92 16.96 14.85C16.7 14.75 16.53 14.74 16.31 14.88C16.11 14.98 15.93 15.13 15.73 15.33L14.97 16.08C14.58 16.46 13.98 16.55 13.52 16.38L13.25 16.26C12.84 16.04 12.36 15.7 11.83 15.25C11.34 14.84 10.84 14.37 10.2 13.74C9.7 13.23 9.22 12.7 8.71 12.12C8.24 11.57 7.9 11.1 7.69 10.71L7.56 10.41C7.5 10.19 7.48 10.06 7.48 9.92C7.48 9.56 7.61 9.24 7.86 8.99L8.61 8.21C8.81 8.01 8.96 7.82 9.06 7.65C9.14 7.52 9.17 7.41 9.17 7.31C9.17 7.23 9.14 7.11 9.09 6.99C9.03 6.82 8.92 6.65 8.78 6.45L6.46 3.17C6.36 3.03 6.24 2.93 6.09 2.86C5.93 2.79 5.76 2.75 5.59 2.75ZM13.95 15.01L13.79 15.69L14.06 14.99C14.02 14.98 13.98 14.99 13.95 15.01Z" fill="currentColor" />
                  <path d="M16.2299 8.51992C16.0399 8.51992 15.8499 8.44992 15.6999 8.29992C15.4099 8.00992 15.4099 7.52992 15.6999 7.23992L19.2399 3.69992C19.5299 3.40992 20.0099 3.40992 20.2999 3.69992C20.5899 3.98992 20.5899 4.46992 20.2999 4.75992L16.7599 8.29992C16.6199 8.43992 16.4199 8.51992 16.2299 8.51992Z" fill="currentColor" />
                  <path d="M19.7699 8.51992C19.5799 8.51992 19.3899 8.44992 19.2399 8.29992L15.6999 4.75992C15.4099 4.46992 15.4099 3.98992 15.6999 3.69992C15.9899 3.40992 16.4699 3.40992 16.7599 3.69992L20.2999 7.23992C20.5899 7.52992 20.5899 8.00992 20.2999 8.29992C20.1499 8.43992 19.9599 8.51992 19.7699 8.51992Z" fill="currentColor" />
                </g>
              </svg>
            </Box>
            {/* Was `No ${activeFilterDef.label.toLowerCase()} calls` —
                broke for "all" ("No all calls") and "abandoned" ("No no
                answer calls", since its label is already "No answer")
                (Rahim, 2026-08-31: "currently it says no all calls it
                really doesnt make sense... for all type of selected
                messages"). Explicit per-filter text instead of interpolating
                the label, same fix shape as MessageCard.jsx's chat empty
                state. */}
            <Typography variant="h6" color="text.secondary">
              {search
                ? "No calls match your search"
                : isLiveFilter
                ? "No live calls right now"
                : activeFilterDef.key === "all"
                ? "No calls found"
                : activeFilterDef.key === "abandoned"
                ? "No unanswered calls found"
                : `No ${activeFilterDef.label.toLowerCase()} calls found`}
            </Typography>
          </Box>
        )}

        {displayRows.map((call) => {
          const liveMatch = liveById.get(call.callId);
          return (
            <LiveCallCard
              key={call.callId}
              call={liveMatch || call}
              live={isLiveFilter || Boolean(liveMatch)}
              answerable={answerableCallIds.includes(call.callId)}
              onClick={handleCardClick}
              onAnswer={inboundAllowed ? handleAnswer : undefined}
              onReject={inboundAllowed ? handleReject : undefined}
              onJoin={handleJoin}
              onOpenRinging={inboundAllowed ? handleOpenRinging : undefined}
              onLifecycleChanged={handleLifecycleChanged}
            />
          );
        })}

        {!isLiveFilter && loading && rows.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!isLiveFilter && hasMore && !loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <Button size="small" onClick={() => loadPage(page + 1, true)}>
              Load more
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
